import React, { useState, useEffect } from 'react';
import { logsApi } from '../../services/api';

function AuditLogList() {
  // Tab state: 'audit' or 'errors'
  const [activeTab, setActiveTab] = useState('audit');

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');

  // Error logs state (Issue #175)
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorTotal, setErrorTotal] = useState(0);
  const [errorPage, setErrorPage] = useState(1);
  const [levelFilter, setLevelFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const perPage = 50;

  // Fetch audit logs
  const fetchAuditLogs = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = { page: auditPage, per_page: perPage };
      if (actionFilter) params.action = actionFilter;
      const response = await logsApi.list(params);
      setAuditLogs(response.data.logs);
      setAuditTotal(response.data.total);
    } catch (err) {
      setError('Fehler beim Laden der Audit-Logs');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch error logs (Issue #175)
  const fetchErrorLogs = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = { page: errorPage, per_page: perPage };
      if (levelFilter) params.log_level = levelFilter;
      if (searchFilter) params.search = searchFilter;
      const response = await logsApi.listErrors(params);
      setErrorLogs(response.data.logs);
      setErrorTotal(response.data.total);
    } catch (err) {
      setError('Fehler beim Laden der Fehler-Logs');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when tab or filters change
  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
    } else {
      fetchErrorLogs();
    }
  }, [activeTab, auditPage, actionFilter, errorPage, levelFilter, searchFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      'login': 'Anmeldung',
      'logout': 'Abmeldung',
      'activate_user': 'Benutzer aktiviert',
      'deactivate_user': 'Benutzer deaktiviert',
      'suspend_user': 'Benutzer gesperrt',
      'unsuspend_user': 'Sperre aufgehoben',
      'delete_user': 'Benutzer gelöscht',
      'delete_post': 'Beitrag gelöscht',
      'delete_object': 'Objekt gelöscht',
      'delete_comment': 'Kommentar gelöscht',
      'resolve_post_report': 'Post-Report bearbeitet',
      'resolve_comment_report': 'Kommentar-Report bearbeitet',
      'create_announcement': 'Ankündigung erstellt',
      'update_announcement': 'Ankündigung aktualisiert',
      'delete_announcement': 'Ankündigung gelöscht',
    };
    return labels[action] || action;
  };

  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 'ERROR': return 'badge-error';
      case 'WARN': return 'badge-warning';
      case 'INFO': return 'badge-info';
      default: return 'badge-default';
    }
  };

  const auditTotalPages = Math.ceil(auditTotal / perPage);
  const errorTotalPages = Math.ceil(errorTotal / perPage);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setErrorPage(1);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>System-Logs</h1>
        <p className="page-subtitle">
          {activeTab === 'audit' ? `${auditTotal} Audit-Einträge` : `${errorTotal} Fehler-Einträge`}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container" style={{ marginBottom: '20px' }}>
        <button
          className={`tab-button ${activeTab === 'audit' ? 'active' : ''}`}
          onClick={() => { setActiveTab('audit'); setError(''); }}
          style={{
            padding: '10px 20px',
            marginRight: '10px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: activeTab === 'audit' ? '#007bff' : '#e9ecef',
            color: activeTab === 'audit' ? 'white' : '#333',
          }}
        >
          Admin-Logs
        </button>
        <button
          className={`tab-button ${activeTab === 'errors' ? 'active' : ''}`}
          onClick={() => { setActiveTab('errors'); setError(''); }}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: activeTab === 'errors' ? '#dc3545' : '#e9ecef',
            color: activeTab === 'errors' ? 'white' : '#333',
          }}
        >
          Backend-Fehler
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        {activeTab === 'audit' ? (
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setAuditPage(1); }}
            className="filter-select"
          >
            <option value="">Alle Aktionen</option>
            <option value="login">Anmeldung</option>
            <option value="logout">Abmeldung</option>
            <option value="activate_user">Benutzer aktiviert</option>
            <option value="deactivate_user">Benutzer deaktiviert</option>
            <option value="suspend_user">Benutzer gesperrt</option>
            <option value="delete_user">Benutzer gelöscht</option>
            <option value="delete_post">Beitrag gelöscht</option>
            <option value="delete_object">Objekt gelöscht</option>
            <option value="delete_comment">Kommentar gelöscht</option>
          </select>
        ) : (
          <>
            <select
              value={levelFilter}
              onChange={(e) => { setLevelFilter(e.target.value); setErrorPage(1); }}
              className="filter-select"
              style={{ marginRight: '10px' }}
            >
              <option value="">Alle Level</option>
              <option value="ERROR">ERROR</option>
              <option value="WARN">WARN</option>
              <option value="INFO">INFO</option>
            </select>
            <input
              type="text"
              placeholder="Suche in Fehlermeldungen..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="search-input"
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                minWidth: '250px',
              }}
            />
          </>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading-container"><div className="loading-spinner"></div></div>
      ) : (
        <>
          {/* Audit Logs Table */}
          {activeTab === 'audit' && (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Zeitpunkt</th>
                      <th>Admin</th>
                      <th>Aktion</th>
                      <th>Ziel</th>
                      <th>IP-Adresse</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.created_at)}</td>
                        <td>{log.admin_username}</td>
                        <td><span className="action-badge">{getActionLabel(log.action)}</span></td>
                        <td>{log.target_type ? `${log.target_type}` : '-'}</td>
                        <td><code>{log.ip_address || '-'}</code></td>
                        <td className="details-cell">
                          {log.details ? (
                            <details>
                              <summary>Details</summary>
                              <pre>{JSON.stringify(log.details, null, 2)}</pre>
                            </details>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {auditTotalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage === 1} className="pagination-btn">← Zurück</button>
                  <span className="pagination-info">Seite {auditPage} von {auditTotalPages}</span>
                  <button onClick={() => setAuditPage(p => Math.min(auditTotalPages, p + 1))} disabled={auditPage === auditTotalPages} className="pagination-btn">Weiter →</button>
                </div>
              )}
            </>
          )}

          {/* Error Logs Table (Issue #175) */}
          {activeTab === 'errors' && (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Zeitpunkt</th>
                      <th>Level</th>
                      <th>Funktion</th>
                      <th>Operation</th>
                      <th>Fehlermeldung</th>
                      <th>Kontext</th>
                      <th>IP-Adresse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorLogs.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          Keine Fehler-Logs vorhanden
                        </td>
                      </tr>
                    ) : (
                      errorLogs.map((log) => (
                        <tr key={log.id} style={{ backgroundColor: log.log_level === 'ERROR' ? '#fff5f5' : 'inherit' }}>
                          <td>{formatDate(log.created_at)}</td>
                          <td>
                            <span
                              className={`level-badge ${getLevelBadgeClass(log.log_level)}`}
                              style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                backgroundColor: log.log_level === 'ERROR' ? '#dc3545' : log.log_level === 'WARN' ? '#ffc107' : '#17a2b8',
                                color: log.log_level === 'WARN' ? '#333' : 'white',
                              }}
                            >
                              {log.log_level}
                            </span>
                          </td>
                          <td><code style={{ fontSize: '12px' }}>{log.function_name}</code></td>
                          <td>{log.operation}</td>
                          <td className="details-cell" style={{ maxWidth: '300px' }}>
                            <details>
                              <summary style={{ cursor: 'pointer', color: '#dc3545' }}>
                                {log.error_message.substring(0, 50)}{log.error_message.length > 50 ? '...' : ''}
                              </summary>
                              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '11px', marginTop: '8px', backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '4px' }}>
                                {log.error_message}
                              </pre>
                            </details>
                          </td>
                          <td style={{ maxWidth: '200px', fontSize: '12px' }}>
                            {log.context ? (
                              <details>
                                <summary style={{ cursor: 'pointer' }}>Kontext</summary>
                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '11px', marginTop: '8px' }}>{log.context}</pre>
                              </details>
                            ) : '-'}
                          </td>
                          <td><code style={{ fontSize: '11px' }}>{log.ip_address || '-'}</code></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {errorTotalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => setErrorPage(p => Math.max(1, p - 1))} disabled={errorPage === 1} className="pagination-btn">← Zurück</button>
                  <span className="pagination-info">Seite {errorPage} von {errorTotalPages}</span>
                  <button onClick={() => setErrorPage(p => Math.min(errorTotalPages, p + 1))} disabled={errorPage === errorTotalPages} className="pagination-btn">Weiter →</button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AuditLogList;
